import { useState, useCallback } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useBarcodeCartStore } from '@/stores/barcodeCartStore';
import BarcodeItemsTable from './components/BarcodeItemsTable';
import PrintCart from './components/PrintCart';
import LabelDesignerModal from './components/LabelDesignerModal';
import { mockCompany } from '@/mocks/masters';
import {
  buildLabelInner,
  printLabelSheet,
  toBarcodeFormat,
  toLabelData,
  LabelFieldFlags,
  LabelLayout,
} from '@/utils/labelPrint';

const STORAGE_KEY = 'barcode_label_settings';

const QUICK_DEFAULT_FIELDS: LabelFieldFlags = {
  companyName: true,
  productName: true,
  brand: true,
  mrp: true,
  saleRate: true,
  barcodeImage: true,
  barcodeNumber: true,
};

type ActiveStep = 1 | 2 | 3;

interface SavedLabelSettings {
  size: string;
  format: string;
  customW: number;
  customH: number;
  fields?: LabelFieldFlags;
  customText?: string;
  companyName?: string;
  layout?: Partial<LabelLayout>;
}

export default function BarcodeManagementPage() {
  const [activeStep, setActiveStep] = useState<ActiveStep>(1);
  const [showDesigner, setShowDesigner] = useState(false);
  const { items } = useBarcodeCartStore();

  const steps = [
    { num: 1, label: 'Browse & Add Items' },
    { num: 2, label: 'Review Cart' },
    { num: 3, label: 'Design & Print' },
  ];

  // Quick Print → uses last saved Label Designer settings (size, fields, layout)
  const handleQuickPrint = useCallback(() => {
    if (items.length === 0) return;

    let saved: SavedLabelSettings = { size: '75x37', format: 'CODE128', customW: 80, customH: 40 };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) saved = { ...saved, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }

    const sizeMap: Record<string, { w: number; h: number }> = {
      '50x25': { w: 50, h: 25 },
      '75x37': { w: 75, h: 37 },
      '100x50': { w: 100, h: 50 },
      custom: { w: saved.customW, h: saved.customH },
    };
    const { w, h } = sizeMap[saved.size] ?? { w: 75, h: 37 };
    const fmt = toBarcodeFormat(saved.format);
    const fields = saved.fields ?? QUICK_DEFAULT_FIELDS;
    const company = saved.companyName || mockCompany.name;

    const labels = items.flatMap((item) => {
      const inner = buildLabelInner(toLabelData(item, company, saved.customText), fields, fmt);
      return Array.from({ length: Math.max(0, item.labelQty || 0) }, () => inner);
    });

    printLabelSheet(labels, w, h, saved.layout);
  }, [items]);

  const handleDesignAndPrint = () => {
    setShowDesigner(true);
    setActiveStep(3);
  };

  return (
    <AppLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col bg-slate-50">
        {/* Page header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Barcode Management</h1>
              <p className="text-xs text-slate-400 mt-0.5">Browse items, build a print cart, and design labels</p>
            </div>
            {items.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-cart-2-line text-sm" />
                </div>
                {items.length} item{items.length !== 1 ? 's' : ''} in cart
              </div>
            )}
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-0 mt-4">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <button
                  onClick={() => setActiveStep(step.num as ActiveStep)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeStep === step.num
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                    activeStep === step.num ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step.num}
                  </span>
                  {step.label}
                </button>
                {idx < steps.length - 1 && (
                  <div className="w-8 h-px bg-slate-200 mx-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 flex flex-col min-h-0 p-6 gap-6">
          <BarcodeItemsTable />
          <PrintCart
            onDesignAndPrint={handleDesignAndPrint}
            onQuickPrint={handleQuickPrint}
          />
        </div>

        {/* Label Designer Modal */}
        {showDesigner && (
          <LabelDesignerModal onClose={() => { setShowDesigner(false); setActiveStep(1); }} />
        )}
      </div>
    </AppLayout>
  );
}
